<template>
  <LoginModal :showModal="loginModalIsTriggered" @close="loginModalIsTriggered = false" />
  <UpdateNodeModal :showModal="isNodeEditing" @close="isNodeEditing = false" :nodeObj="editingNode" @onSubmit="updateNode" />
  <PasswordResetModal :showModal="passwordResetModalIsTriggered" @close="passwordResetModalIsTriggered = false" />
  <section class="flex items-center justify-center w-[95%] h-full">
     <div v-if="isLoggedIn" class="flex justify-center items-start gap-[20px] w-full h-full pb-[100px] mt-[50px]">
     <div
        class="text-center px-[10px] sm:px-5 md:px-10 w-full relative top-0 max-w-[30%] border border-[#E2E8F0] rounded-[8px] p-[20px] bg-[#f9fafb]">
        <HeadingComponent text="Add Node"
            class="text-center text-[33px] font-jost-regular text-blue-light break-all leading-[47px] mb-[10px]" />
        <NodeForm mode="create" :nodeObj="newNode" @onSubmit="addNode" ref="nodeForm" />
      </div>
      <div class="text-center px-[10px] sm:px-5 md:px-10 w-full relative top-0">
          <div v-if="isNodesFetchInProgress" class="flex items-center justify-center mt-[150px]">
            <Loader color="#64748B" width="50px" height="50px" />
          </div>
          <div v-else>
            <HeadingComponent text="Added Nodes List"
              class="text-center text-[33px] font-jost-regular text-blue-light break-all leading-[47px] mb-[30px]" />
              <div class="max-h-[400px] overflow-y-auto" v-if="addedNodes.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th class="text-left">IP</th>
                      <th class="text-left">Port</th>
                      <th class="text-left">Chain Name</th>
                      <th class="text-left">Number of Decimals</th>
                      <th class="text-left">Created At</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(node, index) in addedNodes" :key="index">
                      <td>{{ node.ip }}</td>
                      <td>{{ node.port }}</td>
                      <td>{{ node.chainName }}</td>
                      <td>{{ node.decimalsCount }}</td>
                      <td>{{ formatDate(node.createdAt) }}</td>
                      <td>
                        <button title="Edit node" @click="editNode(node)"><i class="fa fa-pencil mr-[10px] text-blue"></i></button>
                        <button v-if="node.status==='start'" title="Pause node" @click="pauseNode(node)"><i class="fa fa-pause mr-[10px] text-gray"></i></button>
                        <button v-else-if="node.status==='stop'" title="Resume node" @click="resumeNode(node)"><i class="fa fa-play mr-[10px] text-blue-light"></i></button>
                        <button title="Delete node" @click="deleteNode(node)"><i class="fa fa-trash text-red"></i></button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div> 
              <div v-else class="text-center mt-[50px]">
                <p class="text-[20px] font-jost-regular text-gray-plain">Nodes list is empty.</p>
              </div>            
          </div>
      </div>
    </div>
    <div v-else class="text-center pt-[25px] pb-[35px] px-[50px] bg-white border border-[#E2E8F0] rounded-lg shadow-sm">
      <i class="fa fa-lock text-[25px] text-gray-plain mb-[10px]"></i>
      <HeadingComponent text="Please log in to view the admin page." class="text-[24px] font-jost-regular text-gray-plain" />
    </div>
  </section>  
</template>

<script>
import api from '@/helpers/api';
import { defineComponent } from 'vue';
import { formatDate } from '@/helpers/date';
import LoginModal from "@/components/modals/Login.vue";
import PasswordResetModal from "@/components/modals/PasswordReset.vue";
import Loader from '@/components/common/Loader.vue';

import HeadingComponent from '@/components/web/HeadingComponent.vue';
import NodeForm from '@/components/common/NodeForm.vue';
import UpdateNodeModal from '@/components/modals/UpdateNode.vue';

export default defineComponent({
  name: 'AdminPage',
  components: { 
    HeadingComponent,
    LoginModal,
    PasswordResetModal,
    Loader,
    NodeForm,
    UpdateNodeModal,
  },
  data() {
    return {
      newNode: {
        ip: '',
        port: '',
        chainName: '',
        decimalsCount: '',
        enableSystemBasedUpdate: true  
      },
      loginModalIsTriggered: false,
      passwordResetModalIsTriggered: false,
      addedNodes: [],
      isNodesFetchInProgress: false,
      isNodeEditing: false,
      editingNode: null,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.getIsLoggedIn;
    }
  },
  watch: {
    isLoggedIn(val) {
      if (val) {
        this.getAddedNodes();
      }
    }
  },
  created() {
   if (this.isLoggedIn) {
     this.getAddedNodes();
   }
  },
  mounted() {
    this.registerCustomEventsListeners();
  },
  beforeUnmount() {
    if (this.loginHandler) document.removeEventListener('login', this.loginHandler);
    if (this.passwordResetHandler) document.removeEventListener('passwordReset', this.passwordResetHandler);
  },
  methods: {
    async getAddedNodes() {
      if (!this.isLoggedIn) return;
      this.isNodesFetchInProgress = true;
      return api.getAddedNodes(1000, 0)
        .then((res) => {
          const result = res?.data?.result?.items;
          console.log('Nodes details:', result);
          this.addedNodes = Array.isArray(result) ? result : [];
          this.isNodesFetchInProgress = false;
        })
        .catch(() => {
          this.isNodesFetchInProgress = false;
        })
    },
    formatDate(d) {
      return formatDate(d);
    },
    registerCustomEventsListeners() {
      this.loginHandler = () => {
        window.scrollTo(0, 0);
        this.loginModalIsTriggered = true;
      };
      this.passwordResetHandler = () => {
        window.scrollTo(0, 0);
        this.passwordResetModalIsTriggered = true;
      };
      document.addEventListener('login', this.loginHandler);
      document.addEventListener('passwordReset', this.passwordResetHandler);
    },
    addNode(node) {
      // Logic to add a new node
      console.log('Add node clicked');
      const payload = {
        ...node,
        enableSystemBasedUpdate: node.enableSystemBasedUpdate ?? true,
      };

      api.addNode(payload)
        .then((res) => {
          console.log('Node added successfully:', res);
          this.getAddedNodes(); // Refresh the list of nodes
        })
        .catch((err) => {
          console.error('Error adding node:', err);
        });
    },
    editNode(node) {
      // Logic to edit the selected node
      console.log('Edit node clicked for:', node);
      this.isNodeEditing = true;
      this.editingNode = { ...node }; // Create a copy of the node to edit
    },
    pauseNode(node) {
      // Logic to pause the selected node
      console.log('Pause node clicked for:', node);
      api.pauseNode(node.id)
        .then((res) => {
          console.log('Node paused successfully:', res);
          this.addedNodes = this.addedNodes.map((n) => {
            if (n.id === node.id) {
              return { ...n, status: 'stop' }; // Update the status of the paused node
            }
            return n;
          });
        })
        .catch((err) => {
          console.error('Error pausing node:', err);
        });
    },
    resumeNode(node) {
      // Logic to resume the selected node
      console.log('Resume node clicked for:', node);
      api.resumeNode(node.id)
        .then((res) => {
          console.log('Node resumed successfully:', res);
          this.addedNodes = this.addedNodes.map((n) => {
            if (n.id === node.id) {
              return { ...n, status: 'start' }; // Update the status of the resumed node
            }
            return n;
          });
        })
        .catch((err) => {
          console.error('Error resuming node:', err);
        });
    },
    deleteNode(node) {
      // Logic to delete the selected node
      console.log('Delete node clicked for:', node);
      api.deleteNode(node.id)
        .then((res) => {
          console.log('Node deleted successfully:', res);
          this.addedNodes = this.addedNodes.filter(n => n.id !== node.id); // Remove the deleted node from the list
        })
        .catch((err) => {
          console.error('Error deleting node:', err);
        });
    },
    updateNode(node) {
      // Logic to update the selected node
      const updateData = {
        ...node,
        enableSystemBasedUpdate: true,
      };
      console.log('Update node clicked for:', node);
      api.updateNode({ ...updateData, id: this.editingNode.id })
        .then((res) => {
          console.log('Node updated successfully:', res);
          this.getAddedNodes(); // Refresh the list of nodes

          this.$toast.success('Node updated successfully!', {
            position: 'bottom-right',
          });
          setTimeout(() => {
            this.isNodeEditing = false; // Close the modal after a successful update
          }, 1500);
        })
        .catch((err) => {
          console.error('Error updating node:', err);
        });
    },
  }
});
</script>

<style lang="scss" scoped>
.stats-section {
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
//   display: grid;
//   grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); /* Dynamic column widths */
//   gap: 20px; /* Space between cards */
}
table {
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid rgb(229, 231, 235);
  
  thead tr th,
  tbody tr td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid rgb(229, 231, 235);
    border-right: 1px solid rgb(229, 231, 235);
    background-color: #FFFFFF;
    
    &:last-child {
      border-right: none;
    }
  }
  
  thead tr th {
    background-color: #f9fafb;
    font-weight: 600;
    border-bottom: 2px solid rgb(229, 231, 235); // Thicker border for header
  }
  
  tbody tr:last-child td {
    border-bottom: none;
  }
}
</style>
